import React from 'react';
// import { useAnalytics } from 'reaxl-analytics';
import { Heading, Link } from '@argo/ui-elements';
import {
  Section, FlexBox, breakpoints, size,
} from '@argo/principles';
import { useAdaptiveRender } from '@argo/utils';
import Image from 'next/image';
import { akamaiLoader } from '../../../utils';
import { atcBaseUrl } from '../../constants/config';
import styled from '@emotion/styled';
import { useEffect } from 'react';
import { sendClick } from 'reaxl-analytics';

const MainHeadingContainer = styled.div`
  ${({ isMobileViewport }: any) => (isMobileViewport ? '' : `margin-bottom: ${size.md}px`)};
`;

const StyledSubHeading = styled(Heading)`
  line-height: ${size.md}px;
`;

const StyledLink = styled(Link)`
    font-weight: 700;
`;

const FlexBoxContainer = styled(FlexBox)``;

const FlexBoxTrustImageDesktop = styled(FlexBox)``;

const FlexBoxTrustImageMobile = styled.div`
    width: 238px;
`;

const StyledFooterLinks = styled.div`
    margin-top: ${size.lg}px;
`;

const BuildTrustItemFlexBox = styled(FlexBox)`
    width: 288px;
`;

const TrustContentImage = styled(Image)`
    width: 99px;    
`;

export function EnsureTrustImage() {
  return (
    <Image
      loader={akamaiLoader}
      src="psx-react-web/ensure-trust.webp"
      alt="Building Trust"
      width={238}
      height={352}
    />
  );
}

export default function BuildingTrustSection() {
  const { width } = useAdaptiveRender();
  const [isMobileViewport, setIsMobileViewport] = React.useState(false);

  useEffect(() => {
    setIsMobileViewport(width < breakpoints.lg);
  },
  [width]);

  const psxStripeClick = (event: any) => {
    sendClick('psxStripeClick', event);
  };

  const psxLearnMoreFaqsClick = (event: any) => {
    sendClick('psxLearnMoreFaqsClick', event);
  };

  const psxLearnMoreBuyersGuideClick = (event: any) => {
    sendClick('psxLearnMoreBuyersGuideClick', event);
  };

  const psxLearnMoreSellersGuideClick = (event: any) => {
    sendClick('psxLearnMoreSellersGuideClick', event);
  };

  return (
    <Section fullWidth bgColor="#F3F4FA" data-testid="building-trust-container">
      <FlexBoxContainer default={[2, 3]} flexWrap="wrap" justifyContent="center" alignItems="top">
        { !isMobileViewport && (
          <FlexBoxTrustImageDesktop alignItems="center" justifyContent="center">
            <EnsureTrustImage />
          </FlexBoxTrustImageDesktop>
        )}
        <FlexBox
          flowColumn
          flexWrap="wrap"
          justifyContent="center"
          alignItems="baseline"
          verticalSpacing="lg"
        >
          <MainHeadingContainer>
            <Heading headingTag="h2" headingName="SectionHeading">Building Trust: How Do We Do It?</Heading>
          </MainHeadingContainer>
          {isMobileViewport && (
            <FlexBoxTrustImageMobile style={{ alignSelf: 'center' }}>
              <EnsureTrustImage />
            </FlexBoxTrustImageMobile>
          )}
          <FlexBox
            flexDirection={isMobileViewport ? 'column' : 'row'}
            flexWrap={isMobileViewport ? 'wrap' : 'nowrap'}
            alignItems="flex-start"
            justifyContent="center"
          >
            <BuildTrustItemFlexBox
              className="flex-icon-description"
              flexDirection="row"
              alignItems="flex-start"
            >
              <TrustContentImage
                loader={akamaiLoader}
                src="psx-buy-kbb/FamilyFriendly.png"
                className="trust-icon"
                alt="Identity Check"
                width={32}
                height={32}
              />
              <div>
                <StyledSubHeading headingTag="h3" headingName="ContentHeadingExtra">Secure Identity Verification</StyledSubHeading>
                <div>
                  We&apos;ve got you covered. Our verification process checks IP addresses,
                  emails, phone numbers, and uses
                  {' '}
                  <Link
                    href="https://www.stripe.com/identity"
                    onClick={(event: any) => psxStripeClick(event)}
                    target="_blank"
                  >
                    Stripe
                  </Link>
                  {' '}
                  to verify IDs.
                </div>
              </div>
            </BuildTrustItemFlexBox>
            <BuildTrustItemFlexBox
              className="flex-icon-description"
              flexDirection="row"
              alignItems="flex-start"
            >
              <Image
                loader={akamaiLoader}
                src="psx-buy-kbb/kbbVIN.png"
                className="trust-icon"
                alt="History Check"
                width={32}
                height={32}
              />
              <div>
                <StyledSubHeading headingTag="h3" headingName="ContentHeadingExtra">Comprehensive Vehicle History Review</StyledSubHeading>
                <p>
                  Our rigorous checks on ownership and accident history give
                  {' '}
                  you a comprehensive grasp of the vehicle&apos;s background.
                </p>
              </div>
            </BuildTrustItemFlexBox>
          </FlexBox>
          <FlexBox
            flexDirection={isMobileViewport ? 'column' : 'row'}
            flexWrap={isMobileViewport ? 'wrap' : 'nowrap'}
            alignItems="flex-start"
            justifyContent="center"
          >
            <BuildTrustItemFlexBox
              className="flex-icon-description"
              flexDirection="row"
              alignItems="flex-start"
            >
              <Image
                loader={akamaiLoader}
                src="psx-buy-kbb/FraudWatchDepartment.png"
                alt="Secure Payment"
                width={32}
                height={32}
              />
              <div>
                <StyledSubHeading headingTag="h3" headingName="ContentHeadingExtra">Safe and Swift Transactions</StyledSubHeading>
                <p>
                  Buyers seamlessly submit payments online, while sellers securely
                  receive funds via wire transfer, ACH, or credit/debit card.
                </p>
              </div>
            </BuildTrustItemFlexBox>
            <BuildTrustItemFlexBox
              className="flex-icon-description"
              flexDirection="row"
              alignItems="flex-start"
            >
              <Image
                loader={akamaiLoader}
                src="psx-buy-kbb/WindowSticker.png"
                alt="Title Assurance"
                width={32}
                height={32}
              />
              <div>
                <StyledSubHeading headingTag="h3" headingName="ContentHeadingExtra">Title Assurance</StyledSubHeading>
                <p>
                  Rest easy knowing that we pay off the seller&apos;s loan,
                  providing a seamless transfer of ownership to the buyer
                  with the title in hand.
                </p>
              </div>
            </BuildTrustItemFlexBox>
          </FlexBox>
          <StyledFooterLinks>
            Learn more on our
            {' '}
            <StyledLink
              href={`${atcBaseUrl}/marketplace/faqs`}
              onClick={(event: any) => psxLearnMoreFaqsClick(event)}
              target="_blank"
            >
              FAQs
            </StyledLink>
            ,
            {' '}
            <StyledLink
              href={`${atcBaseUrl}/marketplace/buyer-guide`}
              onClick={(event: any) => psxLearnMoreBuyersGuideClick(event)}
              target="_blank"
            >
              Buyer Guide
            </StyledLink>
            , and
            {' '}
            <StyledLink
              href={`${atcBaseUrl}/marketplace/seller-guide`}
              onClick={(event: any) => psxLearnMoreSellersGuideClick(event)}
              target="_blank"
            >
              Seller Guide
            </StyledLink>
          </StyledFooterLinks>
        </FlexBox>
      </FlexBoxContainer>
    </Section>
  );
}
